import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import {
  CampaignListsSchema,
  CampaignSchema,
  LeadListsSchema,
  LeadSchema,
  type CampaignDto,
  type CampaignListsDto,
  type LeadDto,
  type LeadListsDto,
  type MarketingListValueDto,
} from '@taqon/contracts';
import { getData, getPage } from '../../shared/api/client';
import type { HistoryLabels } from '../../shared/audit/AuditTrail';
import { hasCapability, useSession } from '../../shared/session/session';
import { EmptyState, ErrorState, LoadingState, Section } from '../../shared/ui/page';
import { RecordLink } from '../finance/detail';
import { SearchPicker } from '../finance/lookups';

/**
 * Pieces the Lead and Campaign pages share: the API paths, the list values, the campaign picker,
 * the campaign reference that links only where the session may open campaigns, and the
 * "originating lead" panel the client page shows.
 */

export const LEADS = '/api/v1/marketing/leads';
export const CAMPAIGNS = '/api/v1/marketing/campaigns';

/** The lead lists (source, priority, status, qualification, industry), inactive ones included. */
export function useLeadLists() {
  const session = useSession();
  return useQuery({
    queryKey: ['marketing', 'lead-lists'],
    enabled: hasCapability(session.data, 'marketing.lead:view'),
    queryFn: () => getData('/api/v1/marketing/lead-lists', LeadListsSchema),
  });
}

/** The campaign lists (status, channel, target audience), inactive ones included. */
export function useCampaignLists() {
  const session = useSession();
  return useQuery({
    queryKey: ['marketing', 'campaign-lists'],
    enabled: hasCapability(session.data, 'marketing.campaign:view'),
    queryFn: () => getData('/api/v1/marketing/campaign-lists', CampaignListsSchema),
  });
}

/** A stored list code by its label; an unknown code is shown as it is. */
export function valueLabel(
  values: readonly MarketingListValueDto[] | undefined,
  code: string | null,
) {
  if (code === null) return '—';
  const value = values?.find((v) => v.valueCode === code);
  if (value === undefined) return code;
  return value.active ? value.label : `${value.label} (inactive)`;
}

/** How the lead page names the fields its history records (FRD-MKT-015). */
const LEAD_FIELDS: Readonly<Record<string, string>> = {
  companyName: 'Company',
  contactName: 'Contact',
  contactEmail: 'Contact email',
  contactPhone: 'Contact phone',
  sourceCode: 'Source',
  industryCode: 'Industry',
  interest: 'Interest',
  assignedEmployeeId: 'Assigned to',
  priorityCode: 'Priority',
  statusCode: 'Status',
  qualificationStatusCode: 'Qualification',
  lastContactDate: 'Last contact',
  nextAction: 'Next action',
  nextActionDueDate: 'Next action due',
  campaignId: 'Campaign',
  clientId: 'Client',
  conversionDate: 'Conversion date',
};

/** How the campaign page names the fields its history records (FRD-MKT-026). */
const CAMPAIGN_FIELDS: Readonly<Record<string, string>> = {
  name: 'Name',
  objective: 'Objective',
  startDate: 'Start date',
  endDate: 'End date',
  budget: 'Planned budget',
  targetAudienceCode: 'Target audience',
  channelCode: 'Channel',
  ownerEmployeeId: 'Owner',
  statusCode: 'Status',
  attributionModel: 'Attribution model',
  results: 'Results',
};

/**
 * A history that reads like the page: field names as the page shows them, and list-coded values
 * (source, priority, status, …) by their labels, not their codes (ERD §7.6 "(list)").
 */
function historyLabels(
  fields: Readonly<Record<string, string>>,
  lists: Readonly<Record<string, readonly MarketingListValueDto[] | undefined>>,
): HistoryLabels {
  return {
    field: (field) => fields[field],
    value: (field, value) => {
      if (!Object.hasOwn(lists, field)) return undefined;
      const values = lists[field];
      return values === undefined ? undefined : valueLabel(values, value);
    },
  };
}

export function leadHistoryLabels(lists: LeadListsDto | undefined): HistoryLabels {
  return historyLabels(LEAD_FIELDS, {
    sourceCode: lists?.source,
    industryCode: lists?.industry,
    priorityCode: lists?.priority,
    statusCode: lists?.status,
    qualificationStatusCode: lists?.qualificationStatus,
  });
}

export function campaignHistoryLabels(lists: CampaignListsDto | undefined): HistoryLabels {
  return historyLabels(CAMPAIGN_FIELDS, {
    statusCode: lists?.status,
    channelCode: lists?.channel,
    targetAudienceCode: lists?.targetAudience,
  });
}

/** An amount as the API returns it (a decimal string), in AED. */
export function aed(amount: string | null): string {
  return amount === null ? '—' : `AED ${amount}`;
}

const describeCampaign = (c: CampaignDto) => `${c.code} — ${c.name}`;

/** Campaigns by Campaign ID or name, searched on the server (FRD-MKT-012). */
export function CampaignPicker(props: {
  label: string;
  value: CampaignDto | null;
  onChange: (value: CampaignDto | null) => void;
  helperText?: string | undefined;
}) {
  return (
    <SearchPicker
      queryKey="marketing-campaigns"
      schema={CampaignSchema}
      path={(term) =>
        `${CAMPAIGNS}?pageSize=25&sort=-start_date` +
        (term === '' ? '' : `&filter[search]=${encodeURIComponent(term)}`)
      }
      describe={describeCampaign}
      {...props}
    />
  );
}

/** A lead's campaign, linked to its page for a session that may view campaigns. */
export function CampaignRef({
  id,
  code,
  name,
}: {
  id: string | null;
  code: string | null;
  name: string | null;
}) {
  const session = useSession();
  if (id === null) return <>—</>;
  const text = `${code ?? ''} — ${name ?? ''}`;
  return hasCapability(session.data, 'marketing.campaign:view') ? (
    <RecordLink to={`/marketing/campaigns/${id}`}>{text}</RecordLink>
  ) : (
    <>{text}</>
  );
}

/**
 * The lead a client was converted from or linked to (FRD-MKT-030, MKT-031), shown on the client
 * page only for a session that may view leads. Reads the lead register filtered by the client.
 */
export function OriginatingLeads({ clientId }: { clientId: string }) {
  const session = useSession();
  const canView = hasCapability(session.data, 'marketing.lead:view');
  const leads = useQuery({
    queryKey: ['marketing', 'leads', 'client', clientId],
    enabled: canView,
    queryFn: () =>
      getPage(
        `${LEADS}?pageSize=50&sort=code&filter[client_id]=${encodeURIComponent(clientId)}`,
        LeadSchema,
      ),
  });
  if (!canView) return null;
  return (
    <Section title="Originating lead">
      {leads.isPending && <LoadingState label="Loading the originating lead" />}
      {leads.isError && <ErrorState message={leads.error.message} />}
      {leads.data?.items.length === 0 && <EmptyState title="No lead relates to this client" />}
      {leads.data !== undefined && leads.data.items.length > 0 && (
        <Table size="small" aria-label="Originating leads">
          <TableHead>
            <TableRow>
              <TableCell>Lead ID</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Relation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leads.data.items.map((lead) => (
              <TableRow key={lead.id}>
                <TableCell>
                  <RecordLink to={`/marketing/leads/${lead.id}`}>{lead.code}</RecordLink>
                </TableCell>
                <TableCell>{lead.companyName}</TableCell>
                <TableCell>{relationOf(lead)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Section>
  );
}

/** How a lead relates to its client: converted into it, or linked to it. */
export function relationOf(lead: LeadDto): string {
  if (lead.clientId === null) return 'Not converted';
  return lead.conversionDate === null
    ? 'Linked to an existing client'
    : `Converted on ${lead.conversionDate}`;
}

/** Counts by list value, as a compact two-column table. */
export function CountTable({
  label,
  heading,
  rows,
  values,
}: {
  label: string;
  heading: string;
  rows: readonly { code: string | null; count: number }[];
  values: readonly MarketingListValueDto[] | undefined;
}) {
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2">{label}</Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          None
        </Typography>
      ) : (
        <Table size="small" aria-label={label}>
          <TableHead>
            <TableRow>
              <TableCell>{heading}</TableCell>
              <TableCell align="right">Leads</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.code ?? '(none)'}>
                <TableCell>
                  {row.code === null ? 'None recorded' : valueLabel(values, row.code)}
                </TableCell>
                <TableCell align="right">{row.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}
